/**
 * LabFloStatusChip — single-glance status of an order's LabFlo dispatch.
 *
 *   • synced            → green ✓ "LabFlo"        (label: rx_number when present)
 *   • pending (no eRx)  → slate ⏳ "Awaiting eRx"  (only rendered when prop allows)
 *   • blocked           → red ⚠ "Blocked"          (title: blocked_reason)
 *   • not synced        → amber ⏱ "Not synced"     (eRx exists, no block reason, sync failed)
 *
 * Pass the `erx_status` object verbatim from the order list endpoint.
 */
import { AlertCircle, CheckCircle2, Clock, ShieldAlert } from "lucide-react";

export function LabFloStatusChip({ erxId, erxStatus, compact = false, testIdSuffix = "" }) {
  if (!erxId) {
    // No eRx yet — nothing to dispatch. Suppress the chip entirely.
    return null;
  }
  const s = erxStatus || {};
  const synced = !!s.labflo_synced;
  const blocked = !!s.labflo_blocked_reason;
  const rxNumber = s.labflo_rx_number;
  const tid = (suffix) => (testIdSuffix ? `${suffix}-${testIdSuffix}` : suffix);

  const base = "inline-flex items-center gap-1 font-medium rounded-full border";
  const size = compact ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-0.5";

  if (synced) {
    return (
      <span
        className={`${base} ${size} border-emerald-200 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-800 dark:text-emerald-300`}
        title={rxNumber ? `Polomar Rx ${rxNumber}` : "Transmitted to Polomar (LifeFile)"}
        data-testid={tid("labflo-status-synced")}
      >
        <CheckCircle2 className={compact ? "w-2.5 h-2.5" : "w-3 h-3"} />
        Polomar {rxNumber ? `· ${rxNumber}` : ""}
      </span>
    );
  }

  if (blocked) {
    // Humanize known reasons so clinical staff get a one-glance "why".
    const raw = s.labflo_blocked_reason || "blocked";
    const human = raw.startsWith("payment_status=")
      ? `Awaiting payment (${raw.split("=")[1] || "missing"})`
      : raw === "patient_approval_pending"
        ? "Awaiting patient consent"
        : raw.startsWith("missing:")
          ? raw
          : raw.startsWith("lifefile_http_")
            ? `Pharmacy rejected (HTTP ${raw.replace("lifefile_http_", "")})`
            : raw;
    return (
      <span
        className={`${base} ${size} border-rose-300 bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:border-rose-800 dark:text-rose-300`}
        title={`Polomar dispatch blocked: ${raw}${s.labflo_blocked_at ? ` (since ${new Date(s.labflo_blocked_at).toLocaleString()})` : ""}`}
        data-testid={tid("labflo-status-blocked")}
      >
        <ShieldAlert className={compact ? "w-2.5 h-2.5" : "w-3 h-3"} />
        Blocked · {human}
      </span>
    );
  }

  // eRx generated but sync didn't succeed AND no explicit block reason
  // (e.g. transient network error). Treat as "pending retry".
  return (
    <span
      className={`${base} ${size} border-amber-200 bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:border-amber-800 dark:text-amber-300`}
      title="eRx generated but not yet transmitted to Polomar. Resend from Admin → Pharmacy."
      data-testid={tid("labflo-status-pending")}
    >
      <Clock className={compact ? "w-2.5 h-2.5" : "w-3 h-3"} />
      Not sent
    </span>
  );
}

export default LabFloStatusChip;
